import { startAuthentication } from '@simplewebauthn/browser';
import { useEffect, useMemo, useState } from 'react';

import { authClient, type AuthFailureCode } from './auth-client.ts';
import { classifyPasskeyMergeFailure } from './auth-error-classification.ts';
import { deckRouteFromPath } from './deck-route.ts';
import {
  removeTelegramCredential,
  shouldRetainTelegramCredential,
} from './telegram-route.ts';

function telegramToken(): string | null {
  return new URL(window.location.href).searchParams.get('telegram');
}

export function useAuthController() {
  const session = authClient.useSession();
  const [actionError, setActionError] = useState<AuthFailureCode | null>(null);
  const [isActing, setIsActing] = useState(false);
  const [passkeyMergeToken, setPasskeyMergeToken] = useState<string | null>(
    null,
  );
  const [passkeyMergeError, setPasskeyMergeError] = useState<
    'merge-conflict' | null
  >(null);
  const token = telegramToken();
  const pathname = window.location.pathname;
  const deckRoute = useMemo(() => deckRouteFromPath(pathname), [pathname]);
  const deckSlug = deckRoute?.deckSlug ?? null;

  useEffect(() => {
    if (!token || session.isPending) return;
    let cancelled = false;
    setActionError(null);
    setIsActing(true);
    void authClient
      .exchangeTelegramToken(token)
      .then(async () => {
        if (cancelled) return;
        window.history.replaceState(
          null,
          '',
          removeTelegramCredential(window.location.href),
        );
        await session.refetch();
      })
      .catch((error) => {
        if (!cancelled) {
          const code =
            error instanceof Error
              ? (error.message as AuthFailureCode)
              : 'authentication';
          if (!shouldRetainTelegramCredential(code))
            window.history.replaceState(
              null,
              '',
              removeTelegramCredential(window.location.href),
            );
          setActionError(code);
        }
      })
      .finally(() => {
        setIsActing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session.isPending, session.refetch, token]);

  async function run(action: () => Promise<unknown>) {
    setActionError(null);
    setIsActing(true);
    try {
      await action();
      await session.refetch();
    } catch (error) {
      setActionError(
        error instanceof Error
          ? (error.message as AuthFailureCode)
          : 'authentication',
      );
    } finally {
      setIsActing(false);
    }
  }

  async function mergeTelegramAccount() {
    if (!token) return;
    setActionError(null);
    setIsActing(true);
    try {
      await authClient.mergeTelegramAccount(token);
      window.history.replaceState(
        null,
        '',
        removeTelegramCredential(window.location.href),
      );
      await session.refetch();
    } catch (error) {
      setActionError(
        error instanceof Error
          ? (error.message as AuthFailureCode)
          : 'authentication',
      );
    } finally {
      setIsActing(false);
    }
  }

  async function startPasskeyMerge() {
    setActionError(null);
    setPasskeyMergeError(null);
    setIsActing(true);
    try {
      const options = await authClient.passkey.mergeOptions();
      const response = await startAuthentication({ optionsJSON: options });
      const result = await authClient.passkey.mergeVerify(
        options.challenge,
        response,
      );
      if (result.kind === 'confirmation') setPasskeyMergeToken(result.token);
    } catch (error) {
      const { actionError, mergeError } = classifyPasskeyMergeFailure(error);
      if (mergeError) setPasskeyMergeError(mergeError);
      else setActionError(actionError);
    } finally {
      setIsActing(false);
    }
  }

  async function confirmPasskeyMerge() {
    if (!passkeyMergeToken) return;
    setActionError(null);
    setIsActing(true);
    try {
      await authClient.passkey.mergeConfirm(passkeyMergeToken);
      setPasskeyMergeToken(null);
      setPasskeyMergeError(null);
      await session.refetch();
    } catch (error) {
      const { actionError, mergeError } = classifyPasskeyMergeFailure(error);
      if (mergeError) setPasskeyMergeError(mergeError);
      else {
        setPasskeyMergeToken(null);
        setActionError(actionError);
      }
    } finally {
      setIsActing(false);
    }
  }

  async function declinePasskeyMerge() {
    if (!passkeyMergeToken) {
      setPasskeyMergeError(null);
      return;
    }
    setIsActing(true);
    try {
      await authClient.passkey.mergeDecline(passkeyMergeToken);
      setPasskeyMergeToken(null);
      setPasskeyMergeError(null);
    } catch (error) {
      setActionError(
        error instanceof Error
          ? (error.message as AuthFailureCode)
          : 'authentication',
      );
    } finally {
      setIsActing(false);
    }
  }

  function declineTelegramMerge() {
    window.history.replaceState(
      null,
      '',
      removeTelegramCredential(window.location.href),
    );
    setActionError(null);
  }

  return {
    actionError,
    confirmPasskeyMerge,
    deckRoute,
    deckSlug,
    declinePasskeyMerge,
    declineTelegramMerge,
    isActing,
    mergeTelegramAccount,
    passkeyMergeError,
    passkeyMergeToken,
    run,
    session,
    startPasskeyMerge,
    token,
  };
}
